import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './dayTabs.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  addDaysAtSea,
  AppDispatch,
  breakpoints,
  daySelected,
  getActiveSession,
  getLabCDConfig,
  handleKeyDown,
  RootState,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { CruiseDay, DayTabsWrapperProps, SelectedDayFilter } from '@msc-b2c/interfaces/types';
import DayTab from './components/dayTab';
import { Icon } from '@msc-b2c/shared-components';
import DayHeader from './components/dayHeader';
import { getDepartureDate, isXDaysBeforeDeparture } from '../utils/myAreaUtils';

const ALL_PORTS_TAB_ID = 'day-tab-all-ports';
const ALL_PORTS_PANEL_ID = 'day-tabpanel-all-ports';

const getDayTabId = (day: CruiseDay) => `day-tab-${day.date}-${day.port?.code}`;
const getDayTabPanelId = (day: CruiseDay) => `day-tabpanel-${day.date}-${day.port?.code}`;

export function DayTabsWrapper({
  allPortsLabel,
  dayPlaceHolder,
  dayAtSeaLabel,
  scrollLeftIcon,
  scrollRightIcon,
  headerPreTitle,
  headerInfoIcon,
  dayAtSeaInfoLabel,
  arrivalOnlyInfoLabel,
  departureOnlyInfoLabel,
  touchingPortInfoLabel,
}: Readonly<DayTabsWrapperProps>) {
  const dispatch = useDispatch<AppDispatch>();
  const selectedDay = useSelector((state: RootState) => state.myCruise.selectedDayFilter);
  // Pass this value to the store
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dayHeaderPreTitleComposed, setDayHeaderPreTitleComposed] = useState('');
  const [dayHeaderPort, setDayHeaderPort] = useState('');
  const [departureInfoLabel, setDepartureInfoLabel] = useState('');
  const { itinerary: itineraryData } = useSelector((state: RootState) => state.myCruise);
  const activeCruiseId = useSelector((state: RootState) => state.myCruise.activeCruiseId ?? null);

  const scrollContainerRef = useRef<HTMLDivElement>();
  const isMobile = window.innerWidth < breakpoints.lg;

  const { bookingNumber = '' } = getActiveSession();

  // GET BOOKING API CALL
  const { data: booking } = useGetBookingQuery(bookingNumber || skipToken);

  const dayRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (activeIndex > 0 && dayRefs.current[activeIndex]) {
      const container = scrollContainerRef.current;
      const tab = dayRefs.current[activeIndex];

      if (container && tab) {
        const containerRect = container.getBoundingClientRect();
        const tabRect = tab.getBoundingClientRect();

        // Check if the tab is already fully visible
        const isFullyVisible =
          tabRect.left >= containerRect.left && tabRect.right <= containerRect.right;

        if (!isFullyVisible) {
          // Calculate the ideal scroll position without centering
          const scrollLeft =
            tab.offsetLeft - container.offsetLeft - container.clientWidth / 2 + tab.clientWidth / 2;

          container.scrollTo({
            left: Math.min(scrollLeft, container.scrollWidth - container.clientWidth),
            behavior: 'smooth',
          });
        }
      }
    }
  }, [activeIndex]);

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const isAtStart = container.scrollLeft === 0;
      const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 5;

      setShowLeftArrow(!isAtStart);
      setShowRightArrow(!isAtEnd);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 200;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });

      setTimeout(checkScroll, 300);
    }
  };

  useEffect(() => {
    if (itineraryData && itineraryData.days.length > 0) {
      const dayTabs = document.querySelector('.cmp-daytab');
      dayTabs?.classList.remove('cmp-skeleton');
    }
  }, [itineraryData]);

  const updatedDataWithDaysAtSea = itineraryData && addDaysAtSea(itineraryData, dayAtSeaLabel);
  const days = useMemo(() => updatedDataWithDaysAtSea?.days || [], [updatedDataWithDaysAtSea]);

  const focusTabAtIndex = (index: number) => {
    dayRefs.current[index]?.focus();
  };

  const handleAllPortsClick = () => {
    const selectedDay: SelectedDayFilter = {
      portCode: '',
      date: 'allPorts',
    };
    dispatch(daySelected(selectedDay));
    setActiveIndex(0);
    focusTabAtIndex(0);
  };

  const handleDayClick = (day: CruiseDay, index: number) => {
    const selectedDay: SelectedDayFilter = {
      portCode: day.port.code,
      date: day.date,
    };
    dispatch(daySelected(selectedDay));
    setActiveIndex(index + 1);
    focusTabAtIndex(index + 1);
  };

  useEffect(() => {
    checkScroll();
  }, [days]);

  // Update active index based on selectedDay
  useEffect(() => {
    if (!days || days.length === 0) return;

    if (!selectedDay) {
      setActiveIndex(0); // Select "All Ports" by default
    } else {
      // selectedIndex is computed not only based on date but also no portcode
      // this is because if we have 2 ports at the same day the selectedindex
      // might be the same if we dont also put the port code
      const selectedIndex = days.findIndex(
        (day) => day.date === selectedDay.date && day.port.code === selectedDay.portCode
      );
      setActiveIndex(selectedIndex !== -1 ? selectedIndex + 1 : 0);
    }
  }, [selectedDay, days]);

  // helper to format pre title
  const buildPreTitle = (template?: string, index?: number, day?: CruiseDay) => {
    if (!template || !index || !day) return '';
    return template
      .replace('{dayNumber}', String(index))
      .replace('{day}', day.dayOfWeek)
      .toUpperCase();
  };

  const resolveShipPositionLabel = (day: CruiseDay) => {
    const code = day.shipPosition?.code;
    const desc = day.shipPosition?.description;
    const config = getLabCDConfig();
    const match = config.find((c) => code && c.code === Number.parseInt(code));
    return match?.position || desc || '';
  };

  const computeBaseDepartureInfo = (day: CruiseDay) => {
    // Times are already formatted (context-aware) by `addDaysAtSea`.
    if (day?.port?.code === 'SEA' && dayAtSeaInfoLabel) return dayAtSeaInfoLabel;
    if (day?.arrivalTime && !day?.departureTime && arrivalOnlyInfoLabel)
      return arrivalOnlyInfoLabel.replace('{arrivalTime}', day.arrivalTime);
    if (!day?.arrivalTime && day?.departureTime && departureOnlyInfoLabel)
      return departureOnlyInfoLabel.replace('{departureTime}', day.departureTime);
    if (day?.arrivalTime && day?.departureTime && touchingPortInfoLabel)
      return touchingPortInfoLabel
        .replace('{arrivalTime}', day.arrivalTime)
        .replace('{departureTime}', day.departureTime);
    return '';
  };

  useEffect(() => {
    if (activeIndex === 0 || !days || days.length === 0) return;

    const currentDay = days[activeIndex - 1];
    if (!currentDay) return;

    const preTitle = buildPreTitle(headerPreTitle, activeIndex, currentDay);
    if (preTitle) setDayHeaderPreTitleComposed(preTitle);

    if (currentDay?.port?.name) setDayHeaderPort(currentDay.port.name);
    setDepartureInfoLabel('');

    let newDepartureInfo = computeBaseDepartureInfo(currentDay);

    if (newDepartureInfo.includes('{shipPosition}')) {
      const shipPositionLabel = resolveShipPositionLabel(currentDay);
      newDepartureInfo = shipPositionLabel
        ? newDepartureInfo.replace('{shipPosition}', shipPositionLabel)
        : '';
    }

    setDepartureInfoLabel(newDepartureInfo);
  }, [days, selectedDay]);

  if (
    booking &&
    (activeCruiseId || getActiveSession().cruiseId) &&
    isXDaysBeforeDeparture(getDepartureDate(booking))
  ) {
    return null;
  }

  const isAllPortsSelected = !selectedDay || selectedDay.date === 'allPorts';
  const selectedDayData =
    !isAllPortsSelected && selectedDay
      ? days.find((day) => day.date === selectedDay.date && day.port.code === selectedDay.portCode)
      : undefined;

  return (
    <>
      <div className={styles.dayTabsContainer}>
        {showLeftArrow && scrollLeftIcon && !isMobile && (
          <div
            className={`${styles.scrollArrow} ${styles.left}`}
            onClick={() => scroll('left')}
            onKeyDown={(event) => handleKeyDown(event, scroll, ['left'])}
          >
            <div className={styles.scrollIcon}>
              <Icon
                icon={scrollLeftIcon}
                ariaLabel=""
              />
            </div>
          </div>
        )}

        <div
          className={styles.dayTabsWrapper}
          ref={scrollContainerRef}
          onScroll={checkScroll}
          role="tablist"
          aria-orientation="horizontal"
        >
          <DayTab
            ref={(el) => (dayRefs.current[0] = el)}
            id={ALL_PORTS_TAB_ID}
            tabPanelId={ALL_PORTS_PANEL_ID}
            isAllPortTab={true}
            allPortsLabel={allPortsLabel}
            isSelected={isAllPortsSelected}
            onClick={handleAllPortsClick}
          />
          {days.length > 0 &&
            days?.map((day, index) => (
              <DayTab
                key={`${day.date}-${day.port?.code}`}
                ref={(el) => (dayRefs.current[index + 1] = el)}
                id={getDayTabId(day)}
                tabPanelId={getDayTabPanelId(day)}
                isAllPortTab={false}
                dayNumber={index + 1}
                portName={day.port?.name}
                dayPlaceHolder={dayPlaceHolder}
                isSelected={
                  selectedDay?.date === day.date && selectedDay?.portCode === day.port?.code
                }
                onClick={() => handleDayClick(day, index)}
              />
            ))}
        </div>

        {showRightArrow && scrollRightIcon && !isMobile && (
          <div
            className={`${styles.scrollArrow} ${styles.right}`}
            onClick={() => scroll('right')}
            onKeyDown={(event) => handleKeyDown(event, scroll, ['right'])}
          >
            <div className={styles.scrollIcon}>
              <Icon
                icon={scrollRightIcon}
                ariaLabel=""
              />
            </div>
          </div>
        )}
      </div>
      {isAllPortsSelected && (
        <div
          id={ALL_PORTS_PANEL_ID}
          role="tabpanel"
          aria-labelledby={ALL_PORTS_TAB_ID}
          tabIndex={0}
        />
      )}
      {!isAllPortsSelected && selectedDayData && (
        <DayHeader
          id={getDayTabPanelId(selectedDayData)}
          tabId={getDayTabId(selectedDayData)}
          dayPreTitle={dayHeaderPreTitleComposed}
          portName={dayHeaderPort}
          departureInfo={departureInfoLabel}
          icon={headerInfoIcon}
        />
      )}
    </>
  );
}

export default DayTabsWrapper;

DayTabsWrapper.aemResourceType = 'b2c-website-foundation/components/mymsc/daytab';
